import React, {useState, useEffect, useRef, useMemo, useCallback} from 'react'
import {DataTable} from 'primereact/datatable'
import {Column} from 'primereact/column'
import {Toast} from 'primereact/toast'
import {ProgressSpinner} from 'primereact/progressspinner'
import 'primereact/resources/primereact.min.css'
import 'primeicons/primeicons.css'
import {API_URL} from '../../../modules/auth/core/_requests'
import {useThemeMode} from '../../../../_designereyes/partials'
import {debounce} from 'lodash'
import { SortOrder } from 'primereact/api'

/**
 * RawStoreRow represents the response from the backend.
 */
interface RawStoreRow {
  asin: string
  product?: {
    image?: string
    title?: string
    brand?: string
  }
  quantity_sold: number
  quantity_refunded: number
  organic: number
  ppc_units: number
  promos: number
  replacements: number
  reimbursements: number
  revenue: number
  net_revenue: number
  profit?: number
  net_profit?: number
  margin?: number | boolean
  net_margin?: number | boolean
  acos: number | boolean
  tacos?: number | boolean
  roi?: number
  cost: number
  ppc_cost: number
  ppc_sales: number
  store: string // "EC AMZ" or "VINTAGE"
  category?: string
  product_type?: string
  brand_name?: string
}

/**
 * StoreRow is the parsed version for individual rows.
 * (Additional raw* fields are added for sorting/searching.)
 */
interface StoreRow {
  asin: string
  product?: {
    image?: string
    title?: string
    brand?: string
  }
  quantity_sold: number
  quantity_refunded: number
  organic: number
  ppc_units: number
  promos: number
  replacements: number
  reimbursements: number
  revenue: number
  net_revenue: number
  profit: number
  net_profit: number
  margin: number | boolean
  net_margin: number | boolean
  acos: number
  tacos: number | boolean
  roi: number
  cost: number
  ppc_cost: number
  ppc_sales: number
  store: 'EC AMZ' | 'VINTAGE'
  category: string
  product_type: string
  brand_name: string
  // Raw fields used for sorting/searching
  rawRevenue: number
  rawProfit: number
  rawMargin: number
  rawNetMargin: number
  rawACOS: number
  rawTacos: number
  rawROI: number
  rawROAs: number
}

/**
 * TotalRow represents the total row.
 * It also includes breakdown fields from each store.
 */
interface TotalRow {
  asin: '__total__'
  product?: undefined
  store: 'ALL STORES' | 'EC AMZ' | 'VINTAGE'
  quantity_sold: number
  quantity_refunded: number
  organic: number
  ppc_units: number
  revenue: number
  net_revenue: number
  profit: number
  net_profit: number
  margin: number | boolean | null
  net_margin: number | boolean | null
  acos: number
  tacos: number
  roi: number
  cost: number
  ppc_cost: number
  ppc_sales: number
  // Breakdown fields per store
  ecRevenue?: number
  vtRevenue?: number
  ecProfit?: number
  vtProfit?: number
  ecMargin?: number | boolean
  vtMargin?: number | boolean
  ecNetMargin?: number | boolean
  vtNetMargin?: number | boolean
  ecROI?: number
  vtROI?: number
  ecACOS?: number
  vtACOS?: number
  ecTacos?: number
  vtTacos?: number
  ecQuantity?: number
  vtQuantity?: number
  roas: number
  ecROAs: number
  vtROAs: number
  // Raw fields for sorting/searching
  rawRevenue: number
  rawProfit: number
  rawMargin: number
  rawNetMargin: number
  rawACOS: number
  rawTacos: number
  rawROI: number
  rawROAs: number
}

type FinalRow = StoreRow | TotalRow

interface DetailItem {
  label: string
  sales?: number
  refunds?: number
  amount?: number
}

interface Details {
  revenue: DetailItem[]
  fees: DetailItem[]
  costs: DetailItem[]
  adjustments: DetailItem[]
  promotions: DetailItem[]
  profit: number
}

interface FilterCategory {
  selected: boolean
  productTypes: {[key: string]: boolean}
  count: number
  productTypeCounts: {[key: string]: number}
}

type FilterOptions = {[category: string]: FilterCategory}

interface BrandFilterOption {
  selected: boolean
  count: number
}

type BrandFilterOptions = {[brand: string]: BrandFilterOption}

interface FetchResult {
  rows: StoreRow[]
  total: StoreRow
}

/* FORMATTER FUNCTIONS (using any so that we force conversion) */
const fmtCurrency = (v: any): string => {
  if (typeof v !== 'number') return '-'
  return v.toLocaleString('en-US', {style: 'currency', currency: 'USD'})
}

const fmtNumber = (v: any): string => {
  if (typeof v !== 'number') return '-'
  return v.toLocaleString('en-US')
}

const fmtPerc = (v: any): string => {
  if (typeof v !== 'number') return '-'
  return v.toFixed(1) + '%'
}

const fmtRaw = (v: any): string => {
  if (typeof v !== 'number' || isNaN(v) || v === 0) return '-'
  return v.toFixed(1)
}

export const AmazonSalesTable: React.FC = () => {
  console.log(`[${new Date().toISOString()}] Component function called - potential re-render`);

  // --- Applied form state ---
  const {mode} = useThemeMode()
  const bodyBg = mode === 'light' ? '#F4F5F7' : '#2b2b40';
  const bodyLightBg = (mode === 'light' ? "#F4F5F7": '#2b2b40');
  const bodyText = mode === 'light' ? '#000' : '#fff'
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 8)
    return formatDate(d)
  })
  const [toDate, setToDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    return formatDate(d)
  })
  const [ecAmzEnabled, setEcAmzEnabled] = useState(true)
  const [vintageEnabled, setVintageEnabled] = useState(true)

  // --- Temporary form state ---
  const [tempFromDate, setTempFromDate] = useState(fromDate)
  const [tempToDate, setTempToDate] = useState(toDate)
  const [tempEcAmzEnabled, setTempEcAmzEnabled] = useState(ecAmzEnabled)
  const [tempVintageEnabled, setTempVintageEnabled] = useState(vintageEnabled)

  // --- Other UI state ---
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [loading, setLoading] = useState(false)

  // --- Data state ---
  const [ecData, setEcData] = useState<StoreRow[]>([])
  const [vtData, setVtData] = useState<StoreRow[]>([])
  const [ecTotal, setEcTotal] = useState<StoreRow | null>(null)
  const [vtTotal, setVtTotal] = useState<StoreRow | null>(null)
  const [finalData, setFinalData] = useState<FinalRow[]>([])
  const [expandedRows, setExpandedRows] = useState<FinalRow[]>([])
  const [detailsMap, setDetailsMap] = useState<Record<string, Details>>({})
  const toast = useRef<Toast>(null)

  // --- Filtering state ---
  const [filterOptions, setFilterOptions] = useState<FilterOptions>({})
  const [brandFilterOptions, setBrandFilterOptions] = useState<BrandFilterOptions>({})
  const [appliedFilters, setAppliedFilters] = useState<FilterOptions | null>(null)
  const [appliedBrandFilters, setAppliedBrandFilters] = useState<BrandFilterOptions | null>(null)

  // 1. Lazy load the filter content
  const [filterContentLoaded, setFilterContentLoaded] = useState(false);

  // Add these state variables with the other state declarations near the top of your component
  // Add them right after the [filterContentLoaded, setFilterContentLoaded] declaration
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllBrands, setShowAllBrands] = useState(false);

  // Add pagination state
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(50);

  // Add these new state variables at the top of the component with other states
  const [sortField, setSortField] = useState('revenue');
  const [sortOrder, setSortOrder] = useState<SortOrder>(-1 as SortOrder);

  // Helper: format date as yyyy-mm-dd.
  function formatDate(d: Date) {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  // Convert ACOS.
  const parseAcos = (v: number | boolean): number => {
    if (typeof v === 'number' && !isNaN(v)) return v
    return 0
  }

  // Update the customSort function to handle sorting correctly
  const customSort = (event: any) => {
    const { data, field, order } = event;
    
    // Always separate total row
    const totalRows = data.filter((r: FinalRow) => r.asin === '__total__');
    const regularRows = data.filter((r: FinalRow) => r.asin !== '__total__');
    
    regularRows.sort((a: FinalRow, b: FinalRow) => {
      // Handle brand sorting (alphabetical)
      if (field === 'brand') {
        const brandA = (a.product?.brand || '').toLowerCase();
        const brandB = (b.product?.brand || '').toLowerCase();
        return order * brandA.localeCompare(brandB);
      }
      
      // Handle numeric fields
      const getValue = (row: FinalRow) => {
        switch (field) {
          case 'revenue':
            return row.revenue;
          case 'profit':
            return row.profit;
          case 'margin':
            // If profit is negative, exclude from sort (push to end)
            if (row.profit <= 0) return -Infinity;
            // Use first margin value for sorting
            return typeof row.margin === 'number' ? row.margin : -Infinity;
          case 'roi':
            // If profit is negative, exclude from sort
            if (row.profit <= 0) return -Infinity;
            return typeof row.roi === 'number' ? row.roi : -Infinity;
          case 'acos':
            // If ACOS is 0, exclude from sort
            return row.acos === 0 ? -Infinity : row.acos;
          case 'tacos':
            return typeof row.tacos === 'number' && row.tacos !== 0 ? row.tacos : -Infinity;
          case 'roas':
            return row.ppc_cost > 0 ? row.ppc_sales / row.ppc_cost : -Infinity;
          case 'quantity_sold':
            return row.quantity_sold;
          default:
            return 0;
        }
      };

      const valA = getValue(a);
      const valB = getValue(b);
      
      // Handle -Infinity values (excluded from sort)
      if (valA === -Infinity && valB === -Infinity) return 0;
      if (valA === -Infinity) return 1;
      if (valB === -Infinity) return -1;
      
      return order * (valA - valB);
    });

    return [...totalRows, ...regularRows];
  };

  // Update the onSort handler to fix the sorting cycle
  const onSort = (event: any) => {
    console.log('=== onSort Start ===');
    console.log('Sort event:', {
      field: event.sortField,
      order: event.sortOrder,
      currentField: sortField,
      currentOrder: sortOrder
    });
    
    const field = event.sortField;
    
    // If clicking the same field, cycle through: desc -> asc -> none (revenue desc)
    if (field === sortField) {
      if (sortOrder === -1) {
        console.log('Same field: switching to ascending');
        setSortOrder(1 as SortOrder);
      } else {
        console.log('Same field: resetting to default (revenue descending)');
        setSortField('revenue');
        setSortOrder(-1 as SortOrder);
      }
    } else {
      console.log('New field: starting with descending');
      setSortField(field);
      setSortOrder(-1 as SortOrder);
    }
    
    console.log('=== onSort End ===\n');
  };

  // --- Data Fetching ---
  const handleSubmit = async () => {
    setLoading(true)
    
    try {
      // Fetch all data first, without updating state
      let newEcData: StoreRow[] = []
      let newVtData: StoreRow[] = [] 
      let fetchedEcTotal: StoreRow | null = null
      let fetchedVtTotal: StoreRow | null = null
      
      // Explicitly type the fetchPromises array to fix the TypeScript error
      const fetchPromises: Promise<void>[] = []
      
      if (tempEcAmzEnabled) {
        fetchPromises.push(
          fetchStoreRows('ec').then(result => {
            newEcData = result.rows
            fetchedEcTotal = result.total
          })
        )
      }
      
      if (tempVintageEnabled) {
        fetchPromises.push(
          fetchStoreRows('vt').then(result => {
            newVtData = result.rows
            fetchedVtTotal = result.total
          })
        )
      }
      
      // Wait for all fetches to complete
      await Promise.all(fetchPromises)
      
      // Apply filters
      const allRows = [...newEcData, ...newVtData]
      let filteredRows = allRows
      
      if (filterOptions && brandFilterOptions) {
        filteredRows = allRows.filter(row => {
          const cat = row.category || 'UNCATEGORIZED'
          const pt = row.product_type || 'UNCATEGORIZED'
          const brand = row.brand_name || (row.product?.brand || 'UNCATEGORIZED')
          return filterOptions[cat]?.productTypes[pt] && brandFilterOptions[brand]?.selected
        })
      }
      
      // Build total row
      const finalTotal = buildTotalRow(fetchedEcTotal, fetchedVtTotal, filteredRows)
      
      // Prepare all data to update at once
      const updatedState = {
        finalData: [...filteredRows, finalTotal],
        ecData: newEcData,
        vtData: newVtData,
        ecTotal: fetchedEcTotal,
        vtTotal: fetchedVtTotal,
        appliedFilters: filterOptions,
        appliedBrandFilters: brandFilterOptions,
        // Other states you're updating
      };
      
      // Use requestAnimationFrame to batch state updates
      requestAnimationFrame(() => {
        // Update all state in one render cycle
        setAppliedFilters(updatedState.appliedFilters);
        setAppliedBrandFilters(updatedState.appliedBrandFilters);
        setEcData(updatedState.ecData);
        setVtData(updatedState.vtData);
        setEcTotal(updatedState.ecTotal);
        setVtTotal(updatedState.vtTotal);
        setFinalData(updatedState.finalData);
        
        // Only turn off loading after next frame to ensure React has processed updates
        requestAnimationFrame(() => {
          setLoading(false);
        });
      });
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
    }
  }

  const fetchStoreRows = async (which: 'ec' | 'vt'): Promise<FetchResult> => {
    const storeParam = which === 'ec' ? 'ec' : 'vt'
    const resp = await fetch(`${API_URL}/amazon-sales-table/search-asins`, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        minDate: tempFromDate,
        maxDate: tempToDate,
        store: storeParam,
      }),
    })
    
    const arr: RawStoreRow[] = await resp.json()
    const totalRaw = arr.find((x) => x.asin === '__total__')
    const dataRaw = arr.filter((x) => x.asin !== '__total__')
    const storeName: 'EC AMZ' | 'VINTAGE' = which === 'ec' ? 'EC AMZ' : 'VINTAGE'
    
    // Do all mapping and data transformation but don't update state here
    const mappedRows: StoreRow[] = dataRaw.map((r) => ({
      asin: r.asin,
      product: r.product,
      quantity_sold: r.quantity_sold,
      quantity_refunded: r.quantity_refunded,
      organic: r.organic,
      ppc_units: r.ppc_units,
      promos: r.promos,
      replacements: r.replacements,
      reimbursements: r.reimbursements,
      revenue: r.revenue,
      net_revenue: r.net_revenue,
      profit: r.profit ?? 0,
      net_profit: r.net_profit ?? 0,
      margin: typeof r.margin === 'number' ? r.margin : false,
      net_margin: typeof r.net_margin === 'number' ? r.net_margin : false,
      acos: parseAcos(r.acos),
      tacos: typeof r.tacos === 'number' ? r.tacos : false,
      roi: typeof r.roi === 'number' ? r.roi : 0,
      cost: r.cost,
      ppc_cost: r.ppc_cost,
      ppc_sales: r.ppc_sales,
      store: storeName,
      category: r.category ? r.category : 'UNCATEGORIZED',
      product_type: r.product_type ? r.product_type : 'UNCATEGORIZED',
      brand_name: r.brand_name
        ? r.brand_name
        : r.product
        ? r.product.brand || 'UNCATEGORIZED'
        : 'UNCATEGORIZED',
      // Raw fields for sorting/searching:
      rawRevenue: r.revenue,
      rawProfit: r.profit ?? 0,
      rawMargin: typeof r.margin === 'number' ? r.margin : 0,
      rawNetMargin: typeof r.net_margin === 'number' ? r.net_margin : 0,
      rawACOS: parseAcos(r.acos),
      rawTacos: typeof r.tacos === 'number' ? r.tacos : 0,
      rawROI: typeof r.roi === 'number' ? r.roi : 0,
      rawROAs: r.ppc_cost > 0 ? r.ppc_sales / r.ppc_cost : 0,
    }))
    
    const mappedTotal: StoreRow = totalRaw
      ? {
          asin: totalRaw.asin,
          product: undefined,
          quantity_sold: totalRaw.quantity_sold,
          quantity_refunded: totalRaw.quantity_refunded,
          organic: totalRaw.organic,
          ppc_units: totalRaw.ppc_units,
          promos: totalRaw.promos,
          replacements: totalRaw.replacements,
          reimbursements: totalRaw.reimbursements,
          revenue: totalRaw.revenue,
          net_revenue: totalRaw.net_revenue,
          profit: totalRaw.profit ?? 0,
          net_profit: totalRaw.net_profit ?? 0,
          margin: typeof totalRaw.margin === 'number' ? totalRaw.margin : false,
          net_margin: typeof totalRaw.net_margin === 'number' ? totalRaw.net_margin : false,
          acos: parseAcos(totalRaw.acos),
          tacos: typeof totalRaw.tacos === 'number' ? totalRaw.tacos : false,
          roi: typeof totalRaw.roi === 'number' ? totalRaw.roi : 0,
          cost: totalRaw.cost,
          ppc_cost: totalRaw.ppc_cost,
          ppc_sales: totalRaw.ppc_sales,
          store: storeName,
          category: totalRaw.category ? totalRaw.category : 'UNCATEGORIZED',
          product_type: totalRaw.product_type ? totalRaw.product_type : 'UNCATEGORIZED',
          brand_name: totalRaw.brand_name
            ? totalRaw.brand_name
            : totalRaw.product
            ? totalRaw.product.brand || 'UNCATEGORIZED'
            : 'UNCATEGORIZED',
          rawRevenue: totalRaw.revenue,
          rawProfit: totalRaw.profit ?? 0,
          rawMargin: typeof totalRaw.margin === 'number' ? totalRaw.margin : 0,
          rawNetMargin: typeof totalRaw.net_margin === 'number' ? totalRaw.net_margin : 0,
          rawACOS: parseAcos(totalRaw.acos),
          rawTacos: typeof totalRaw.tacos === 'number' ? totalRaw.tacos : 0,
          rawROI: typeof totalRaw.roi === 'number' ? totalRaw.roi : 0,
          rawROAs: totalRaw.ppc_cost > 0 ? totalRaw.ppc_sales / totalRaw.ppc_cost : 0,
        }
      : {
          asin: '__total__',
          product: undefined,
          quantity_sold: 0,
          quantity_refunded: 0,
          organic: 0,
          ppc_units: 0,
          promos: 0,
          replacements: 0,
          reimbursements: 0,
          revenue: 0,
          net_revenue: 0,
          profit: 0,
          net_profit: 0,
          margin: false,
          net_margin: false,
          acos: 0,
          tacos: false,
          roi: 0,
          cost: 0,
          ppc_cost: 0,
          ppc_sales: 0,
          store: storeName,
          category: 'UNCATEGORIZED',
          product_type: 'UNCATEGORIZED',
          brand_name: 'UNCATEGORIZED',
          rawRevenue: 0,
          rawProfit: 0,
          rawMargin: 0,
          rawNetMargin: 0,
          rawACOS: 0,
          rawTacos: 0,
          rawROI: 0,
          rawROAs: 0,
        }
    return {rows: mappedRows, total: mappedTotal}
  }

  const buildTotalRow = (
    ecT: StoreRow | null,
    vtT: StoreRow | null,
    combinedRows: StoreRow[]
  ): TotalRow => {
    if (ecT && !vtT) {
      return {
        ...ecT,
        asin: '__total__',
        product: undefined,
        roas: ecT.ppc_cost > 0 ? ecT.ppc_sales / ecT.ppc_cost : 0,
        ecROAs: ecT.ppc_cost > 0 ? ecT.ppc_sales / ecT.ppc_cost : 0,
        vtROAs: 0,
        ecTacos: typeof ecT.tacos === 'number' ? ecT.tacos : 0,
        vtTacos: 0,
        rawRevenue: ecT.rawRevenue,
        rawProfit: ecT.rawProfit,
        rawMargin: typeof ecT.rawMargin === 'number' ? ecT.rawMargin : 0,
        rawNetMargin: typeof ecT.rawNetMargin === 'number' ? ecT.rawNetMargin : 0,
        rawACOS: ecT.rawACOS,
        rawTacos: ecT.rawTacos,
        rawROI: ecT.rawROI,
        rawROAs: ecT.rawROAs,
      } as TotalRow
    }
    if (!ecT && vtT) {
      return {
        ...vtT,
        asin: '__total__',
        product: undefined,
        roas: vtT.ppc_cost > 0 ? vtT.ppc_sales / vtT.ppc_cost : 0,
        ecROAs: 0,
        vtROAs: vtT.ppc_cost > 0 ? vtT.ppc_sales / vtT.ppc_cost : 0,
        ecTacos: 0,
        vtTacos: typeof vtT.tacos === 'number' ? vtT.tacos : 0,
        rawRevenue: vtT.rawRevenue,
        rawProfit: vtT.rawProfit,
        rawMargin: typeof vtT.rawMargin === 'number' ? vtT.rawMargin : 0,
        rawNetMargin: typeof vtT.rawNetMargin === 'number' ? vtT.rawNetMargin : 0,
        rawACOS: vtT.rawACOS,
        rawTacos: vtT.rawTacos,
        rawROI: vtT.rawROI,
        rawROAs: vtT.rawROAs,
      } as TotalRow
    }
    if (ecT && vtT) {
      const combinedRevenue = ecT.revenue + vtT.revenue
      const combinedProfit = ecT.profit + vtT.profit
      const combinedMargin =
        combinedRevenue > 0 && typeof ecT.margin === 'number' && typeof vtT.margin === 'number'
          ? (ecT.revenue * ecT.margin + vtT.revenue * vtT.margin) / combinedRevenue
          : null
      const combinedNetMargin =
        combinedRevenue > 0 &&
        typeof ecT.net_margin === 'number' &&
        typeof vtT.net_margin === 'number'
          ? (ecT.revenue * ecT.net_margin + vtT.revenue * vtT.net_margin) / combinedRevenue
          : null
      const combinedROI =
        combinedRevenue > 0 ? (ecT.revenue * ecT.roi + vtT.revenue * vtT.roi) / combinedRevenue : 0
      const combinedACOS =
        combinedRevenue > 0
          ? (ecT.revenue * ecT.acos + vtT.revenue * vtT.acos) / combinedRevenue
          : 0
      const combinedTacos =
        (typeof ecT.tacos === 'number' ? ecT.tacos : 0) +
        (typeof vtT.tacos === 'number' ? vtT.tacos : 0)
      const combinedROAs =
        ecT.ppc_cost + vtT.ppc_cost > 0
          ? (ecT.ppc_sales + vtT.ppc_sales) / (ecT.ppc_cost + vtT.ppc_cost)
          : 0
      return {
        asin: '__total__',
        store: 'ALL STORES',
        quantity_sold: ecT.quantity_sold + vtT.quantity_sold,
        quantity_refunded: ecT.quantity_refunded + vtT.quantity_refunded,
        organic: ecT.organic + vtT.organic,
        ppc_units: ecT.ppc_units + vtT.ppc_units,
        revenue: combinedRevenue,
        net_revenue: ecT.net_revenue + vtT.net_revenue,
        profit: combinedProfit,
        net_profit: ecT.net_profit + vtT.net_profit,
        margin: combinedMargin,
        net_margin: combinedNetMargin,
        acos: combinedACOS,
        tacos: combinedTacos,
        roi: combinedROI,
        cost: ecT.cost + vtT.cost,
        ppc_cost: ecT.ppc_cost + vtT.ppc_cost,
        ppc_sales: ecT.ppc_sales + vtT.ppc_sales,
        ecRevenue: ecT.revenue,
        vtRevenue: vtT.revenue,
        ecProfit: ecT.profit,
        vtProfit: vtT.profit,
        ecMargin: ecT.margin,
        vtMargin: vtT.margin,
        ecNetMargin: ecT.net_margin,
        vtNetMargin: vtT.net_margin,
        ecROI: ecT.roi,
        vtROI: vtT.roi,
        ecACOS: ecT.acos,
        vtACOS: vtT.acos,
        ecTacos: typeof ecT.tacos === 'number' ? ecT.tacos : 0,
        vtTacos: typeof vtT.tacos === 'number' ? vtT.tacos : 0,
        roas: combinedROAs,
        ecROAs: ecT.ppc_cost > 0 ? ecT.ppc_sales / ecT.ppc_cost : 0,
        vtROAs: vtT.ppc_cost > 0 ? vtT.ppc_sales / vtT.ppc_cost : 0,
        rawRevenue: ecT.rawRevenue + vtT.rawRevenue,
        rawProfit: ecT.rawProfit + vtT.rawProfit,
        rawMargin: combinedMargin ?? 0,
        rawNetMargin: combinedNetMargin ?? 0,
        rawACOS: combinedACOS,
        rawTacos: combinedTacos,
        rawROI: combinedROI,
        rawROAs: combinedROAs,
      }
    }
    const fallback = {
      asin: '__total__',
      store: 'ALL STORES',
      quantity_sold: 0,
      quantity_refunded: 0,
      organic: 0,
      ppc_units: 0,
      revenue: 0,
      net_revenue: 0,
      profit: 0,
      net_profit: 0,
      margin: 0,
      net_margin: 0,
      acos: 0,
      tacos: 0,
      roi: 0,
      cost: 0,
      ppc_cost: 0,
      ppc_sales: 0,
      roas: 0,
      ecROAs: 0,
      vtROAs: 0,
      ecTacos: 0,
      vtTacos: 0,
      rawRevenue: 0,
      rawProfit: 0,
      rawMargin: 0,
      rawNetMargin: 0,
      rawACOS: 0,
      rawTacos: 0,
      rawROI: 0,
      rawROAs: 0,
    } as TotalRow
    return fallback
  }

  // --- Row Expansion Handlers & Details Rendering ---
  const onRowExpand = (e: any) => {
    const row = e.data as FinalRow
    setExpandedRows([...expandedRows, row])
    fetchBreakdown(row.asin)
  }

  const onRowCollapse = (e: any) => {
    const row = e.data as FinalRow
    setExpandedRows(expandedRows.filter((x) => x.asin !== row.asin))
  }

  const fetchBreakdown = async (asin: string) => {
    try {
      let store = 'none'
      if (ecAmzEnabled && vintageEnabled) store = 'both'
      else if (ecAmzEnabled) store = 'ec'
      else if (vintageEnabled) store = 'vt'
      if (store === 'none' || !fromDate || !toDate) return
      const url =
        asin === '__total__'
          ? `${API_URL}/amazon-sales-table/total-breakdown?dateMin=${fromDate}&dateMax=${toDate}&store=${store}`
          : `${API_URL}/amazon-sales-table/asin-breakdown?asin=${asin}&dateMin=${fromDate}&dateMax=${toDate}&store=${store}`
      const resp = await fetch(url)
      const json = await resp.json()
      const details = json.data || {}
      setDetailsMap((prev) => ({...prev, [asin]: details}))
    } catch (err) {
      console.error('Error fetching breakdown:', err)
    }
  }

  const rowExpansionTemplate = (rowData: FinalRow) => {
    const dt = detailsMap[rowData.asin]
    if (!dt) {
      return <div style={{color: bodyText , background: (mode === 'light'? bodyLightBg : bodyBg) }}>Loading breakdown...</div>
    }
    return (
      <div style={{backgroundColor: (mode === 'light' ? bodyLightBg : bodyBg), color: bodyText , padding: '0.5rem'}}>
        <pre>{JSON.stringify(dt, null, 2)}</pre>
      </div>
    )
  }

  // --- Column Body Templates ---
  const storeBodyTemplate = (row: FinalRow) => {
    if (row.asin === '__total__') {
      return <span style={{color: (mode === 'light' ? '#383508' : '#ffa'), fontWeight: 'bold'}}>{row.store}</span>
    }
    const color = row.store === 'EC AMZ' ? 'skyblue' : row.store === 'VINTAGE' ? 'pink' : (mode === 'light' ? '#383508' : '#ffa')
    return <span style={{fontWeight: 'bold', color}}>{row.store}</span>
  }

  const imageBodyTemplate = (row: FinalRow) => {
    if (row.asin === '__total__') return null
    if (row.product?.image) {
      return (
        <div
          style={{
            width: '80px',
            height: '50px',
            background: 'white',
            borderRadius: '15px',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <img
            src={row.product.image}
            alt={row.product.title}
            style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}}
            onError={(e) => {
              ;(e.currentTarget as HTMLImageElement).src = 'https://via.placeholder.com/100'
            }}
          />
        </div>
      )
    }
    return null
  }

  const brandBodyTemplate = (row: FinalRow) => {
    if (row.asin === '__total__') return null
    return row.product?.brand || null
  }

  const unitsAndStatsBodyTemplate = (row: FinalRow) => {
    const containerStyle: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }
    const unitsStyle: React.CSSProperties = {
      width: '30%',
      textAlign: 'right',
      paddingRight: '5px',
      minWidth: '80px',
    }
    const statsStyle: React.CSSProperties = {
      width: '70%',
      textAlign: 'left',
      lineHeight: '1.2',
    }

    let unitsContent
    if (row.asin === '__total__') {
      const t = row as TotalRow
      unitsContent = (
        <>
          <div style={{fontWeight: 'bold', color: (mode === 'light' ? '#383508' : '#fff'), fontSize: '22px'}}>{t.quantity_sold}</div>
          {ecAmzEnabled && vintageEnabled && (
            <>
              <div style={{fontSize: '12px', color: 'skyblue'}}>{t.ecQuantity}</div>
              <div style={{fontSize: '12px', color: 'pink'}}>{t.vtQuantity}</div>
            </>
          )}
        </>
      )
    } else {
      unitsContent = <span style={{fontSize: '22px'}}>{(row as StoreRow).quantity_sold}</span>
    }

    const statsContent: JSX.Element[] = []
    if (row.organic > 0) {
      statsContent.push(
        <div key='org' style={{color: 'lightgreen', fontSize: '13px'}}>
          {row.organic} organic
        </div>
      )
    }
    if ((row as StoreRow).ppc_units > 0) {
      statsContent.push(
        <div key='ppc' style={{color: '#FFB347', fontSize: '13px'}}>
          {(row as StoreRow).ppc_units} ppc
        </div>
      )
    }
    if (row.quantity_refunded > 0) {
      statsContent.push(
        <div key='ref' style={{color: '#FF6961', fontSize: '13px'}}>
          {row.quantity_refunded} refunds
        </div>
      )
    }
    return (
      <div style={containerStyle}>
        <div style={unitsStyle}>{unitsContent}</div>
        <div style={statsStyle}>{statsContent}</div>
      </div>
    )
  }

  const revenueBodyTemplate = (row: FinalRow) => {
    if (row.asin !== '__total__') {
      return fmtCurrency(row.revenue)
    }
    const t = row as TotalRow
    return (
      <div style={{whiteSpace: 'nowrap'}}>
        <div style={{fontWeight: 'bold', fontSize: '16px', color: (mode === 'light' ? '#383508' : '#fff')}}>
          {fmtCurrency(t.revenue)}
        </div>
        {ecAmzEnabled && vintageEnabled && t.ecRevenue != null && t.vtRevenue != null && (
          <>
            <div style={{fontSize: '12px', color: 'skyblue'}}>EC: {fmtCurrency(t.ecRevenue)}</div>
            <div style={{fontSize: '12px', color: 'pink'}}>VT: {fmtCurrency(t.vtRevenue)}</div>
          </>
        )}
      </div>
    )
  }

  const profitBodyTemplate = (row: FinalRow) => {
    if (row.asin !== '__total__') {
      return fmtCurrency((row as StoreRow).profit)
    }
    const t = row as TotalRow
    return (
      <div style={{whiteSpace: 'nowrap'}}>
        <div style={{fontWeight: 'bold', fontSize: '16px', color: (mode === 'light' ? '#383508' : '#fff')}}>
          {fmtCurrency(t.profit)}
        </div>
        {ecAmzEnabled && vintageEnabled && t.ecProfit != null && t.vtProfit != null && (
          <>
            <div style={{fontSize: '12px', color: 'skyblue'}}>EC: {fmtCurrency(t.ecProfit)}</div>
            <div style={{fontSize: '12px', color: 'pink'}}>VT: {fmtCurrency(t.vtProfit)}</div>
          </>
        )}
      </div>
    )
  }

  const marginCombinedBodyTemplate = (row: FinalRow) => {
    if (row.asin === '__total__') {
      const t = row as TotalRow
      return (
        <div style={{whiteSpace: 'nowrap', textAlign: 'center'}}>
          <div style={{fontWeight: 'bold', fontSize: '16px', color: (mode === 'light' ? '#383508' : '#fff')}}>
            {fmtPerc(t.margin)} / {fmtPerc(t.net_margin)}
          </div>
          {ecAmzEnabled && vintageEnabled && t.ecMargin != null && t.vtMargin != null && (
            <>
              <div style={{fontSize: '12px', color: 'skyblue'}}>
                EC: {fmtPerc(t.ecMargin)} / {fmtPerc(t.ecNetMargin)}
              </div>
              <div style={{fontSize: '12px', color: 'pink'}}>
                VT: {fmtPerc(t.vtMargin)} / {fmtPerc(t.vtNetMargin)}
              </div>
            </>
          )}
        </div>
      )
    } else {
      const r = row as StoreRow
      return (
        <div style={{whiteSpace: 'nowrap', textAlign: 'center'}}>
          <div style={{fontWeight: 'bold', fontSize: '16px'}}>{r.profit <= 0 ? '-' : fmtPerc(r.margin)}</div>
          <div style={{fontSize: '12px'}}>{r.profit <= 0 ? '-' : fmtPerc(r.net_margin)}</div>
        </div>
      )
    }
  }

  const roiBodyTemplate = (row: FinalRow) => {
    if (row.asin !== '__total__') {
      const r = row as StoreRow
      return r.profit <= 0 ? '-' : fmtPerc(r.roi)
    }
    const t = row as TotalRow
    return (
      <div style={{whiteSpace: 'nowrap'}}>
        <div style={{fontWeight: 'bold', fontSize: '16px', color: (mode === 'light' ? '#383508' : '#fff')}}>{fmtPerc(t.roi)}</div>
        {ecAmzEnabled && vintageEnabled && t.ecROI != null && t.vtROI != null && (
          <>
            <div style={{fontSize: '12px', color: 'skyblue'}}>EC: {fmtPerc(t.ecROI)}</div>
            <div style={{fontSize: '12px', color: 'pink'}}>VT: {fmtPerc(t.vtROI)}</div>
          </>
        )}
      </div>
    )
  }

  const acosBodyTemplate = (row: FinalRow) => {
    if (row.asin !== '__total__') {
      return row.acos === 0 ? '-' : fmtPerc(row.acos);
    }
    const t = row as TotalRow;
    return (
      <div style={{whiteSpace: 'nowrap'}}>
        <div style={{fontWeight: 'bold', fontSize: '16px', color: (mode === 'light' ? '#383508' : '#fff')}}>
          {t.acos === 0 ? '-' : fmtPerc(t.acos)}
        </div>
        {ecAmzEnabled && vintageEnabled && t.ecACOS != null && t.vtACOS != null && (
          <>
            <div style={{fontSize: '12px', color: 'skyblue'}}>
              EC: {t.ecACOS === 0 ? '-' : fmtPerc(t.ecACOS)}
            </div>
            <div style={{fontSize: '12px', color: 'pink'}}>
              VT: {t.vtACOS === 0 ? '-' : fmtPerc(t.vtACOS)}
            </div>
          </>
        )}
      </div>
    );
  };

  const tacosBodyTemplate = (row: FinalRow) => {
    const fmtTacos = (v: any): string => {
      if (typeof v !== 'number' || v === 0) return '-'
      return v.toString()
    }
    if (row.asin !== '__total__') {
      return fmtTacos((row as StoreRow).tacos)
    }
    const t = row as TotalRow
    return (
      <div style={{whiteSpace: 'nowrap', textAlign: 'center'}}>
        <div style={{fontWeight: 'bold', fontSize: '16px', color: (mode === 'light' ? '#383508' : '#fff')}}>{fmtTacos(t.tacos)}</div>
        {ecAmzEnabled && vintageEnabled && t.ecTacos != null && t.vtTacos != null && (
          <>
            <div style={{fontSize: '12px', color: 'skyblue'}}>EC: {fmtTacos(t.ecTacos)}</div>
            <div style={{fontSize: '12px', color: 'pink'}}>VT: {fmtTacos(t.vtTacos)}</div>
          </>
        )}
      </div>
    )
  }

  const roasBodyTemplate = (row: FinalRow) => {
    const fmtRoas = (v: any): string => {
      if (typeof v !== 'number' || isNaN(v) || v === 0) return '-'
      return v.toFixed(1)
    }
    if (row.asin !== '__total__') {
      const r = row as StoreRow
      const roas = r.ppc_cost > 0 ? r.ppc_sales / r.ppc_cost : 0
      return fmtRoas(roas)
    }
    const t = row as TotalRow
    return (
      <div style={{whiteSpace: 'nowrap', textAlign: 'center'}}>
        <div style={{fontWeight: 'bold', fontSize: '16px', color: (mode === 'light' ? '#383508' : '#fff')}}>{fmtRoas(t.roas)}</div>
        {ecAmzEnabled && vintageEnabled && t.ecROAs != null && t.vtROAs != null && (
          <>
            <div style={{fontSize: '12px', color: 'skyblue'}}>EC: {fmtRoas(t.ecROAs)}</div>
            <div style={{fontSize: '12px', color: 'pink'}}>VT: {fmtRoas(t.vtROAs)}</div>
          </>
        )}
      </div>
    )
  }

  // --- Filtering Handlers ---
  useEffect(() => {
    const computeFilterOptions = (rows: StoreRow[]): FilterOptions => {
      const options: FilterOptions = {}
      rows.forEach((row) => {
        const cat = row.category || 'UNCATEGORIZED'
        const pt = row.product_type || 'UNCATEGORIZED'
        if (!options[cat]) {
          options[cat] = {selected: true, productTypes: {}, count: 0, productTypeCounts: {}}
        }
        options[cat].count += 1
        if (options[cat].productTypes[pt] === undefined) {
          options[cat].productTypes[pt] = true
          options[cat].productTypeCounts[pt] = 0
        }
        options[cat].productTypeCounts[pt] += 1
      })
      return options
    }
    const newFilterOptions = computeFilterOptions([...ecData, ...vtData])
    setFilterOptions(newFilterOptions)
    setAppliedFilters(newFilterOptions)
  }, [ecData, vtData])

  useEffect(() => {
    const computeBrandFilterOptions = (rows: StoreRow[]): BrandFilterOptions => {
      const options: BrandFilterOptions = {}
      rows.forEach((row) => {
        const brand = row.brand_name || 'UNCATEGORIZED'
        if (!options[brand]) {
          options[brand] = {selected: true, count: 0}
        }
        options[brand].count += 1
      })
      return options
    }
    const newBrandFilterOptions = computeBrandFilterOptions([...ecData, ...vtData])
    setBrandFilterOptions(newBrandFilterOptions)
    setAppliedBrandFilters(newBrandFilterOptions)
  }, [ecData, vtData])

  useEffect(() => {
    if (appliedFilters && appliedBrandFilters) {
      let combined = [...ecData, ...vtData].filter((row) => {
        const cat = row.category || 'UNCATEGORIZED'
        const pt = row.product_type || 'UNCATEGORIZED'
        const brand =
          row.brand_name || (row.product ? row.product.brand || 'UNCATEGORIZED' : 'UNCATEGORIZED')
        return appliedFilters[cat]?.productTypes[pt] && appliedBrandFilters[brand]?.selected
      })
      const finalTotal = buildTotalRow(ecTotal, vtTotal, combined)
      setFinalData([...combined, finalTotal])
    }
  }, [ecData, vtData, appliedFilters, appliedBrandFilters, ecTotal, vtTotal])

  // Memoize the category checkbox handling
  const handleCategoryToggle = useCallback((cat: string, checked: boolean) => {
    console.time('category-toggle');
    console.log(`Category toggle start: ${cat} -> ${checked}`);
    
    console.log(`Current filter options size: ${Object.keys(filterOptions).length} categories`);
    
    setFilterOptions(prev => {
      console.log("Starting state update");
      const newOptions = {...prev};
      if (newOptions[cat]) {
        console.log(`Updating category ${cat}`);
        newOptions[cat].selected = checked;
      }
      console.log("Finished creating new state");
      return newOptions;
    });
    
    console.log(`Category toggle end: ${cat}`);
    console.timeEnd('category-toggle');
  }, []);

  // Memoize the product type checkbox handling
  const handleProductTypeToggle = useCallback((cat: string, pt: string, checked: boolean) => {
    setFilterOptions((prev) => {
      const newOptions = {...prev}
      if (newOptions[cat]) {
        newOptions[cat].productTypes[pt] = checked
        const allSelected = Object.values(newOptions[cat].productTypes).every((v) => v === true)
        newOptions[cat].selected = allSelected
      }
      return newOptions
    })
  }, []);

  const handleBrandToggle = (brand: string, checked: boolean) => {
    setBrandFilterOptions((prev) => {
      const newOptions = {...prev}
      if (newOptions[brand]) {
        newOptions[brand].selected = checked
      }
      return newOptions
    })
  }

  const handleToggleAllCategories = (checked: boolean) => {
    setFilterOptions((prev) => {
      const newOptions: FilterOptions = {}
      Object.keys(prev).forEach((cat) => {
        newOptions[cat] = {
          ...prev[cat],
          selected: checked,
          productTypes: Object.fromEntries(
            Object.entries(prev[cat].productTypes).map(([pt]) => [pt, checked])
          ),
          count: prev[cat].count,
          productTypeCounts: prev[cat].productTypeCounts,
        }
      })
      return newOptions
    })
  }

  const handleToggleAllBrands = (checked: boolean) => {
    setBrandFilterOptions((prev) => {
      const newOptions: BrandFilterOptions = {}
      Object.keys(prev).forEach((brand) => {
        newOptions[brand] = {...prev[brand], selected: checked}
      })
      return newOptions
    })
  }

  const allCategoriesSelected = useMemo(() => 
    Object.keys(filterOptions).length > 0 &&
    Object.keys(filterOptions).every(
      (cat) =>
        filterOptions[cat].selected &&
        Object.values(filterOptions[cat].productTypes).every((v) => v === true)
    ), [filterOptions]
  );

  const allBrandsSelected = useMemo(() => 
    Object.keys(brandFilterOptions).length > 0 &&
    Object.keys(brandFilterOptions).every((brand) => brandFilterOptions[brand].selected), 
    [brandFilterOptions]
  );

  // Update the applyFilter function to reset sort to default
  const applyFilter = () => {
    setAppliedFilters(filterOptions);
    setAppliedBrandFilters(brandFilterOptions);
    setShowFilterDropdown(false);
    // Reset sort to default
    setSortField('revenue');
    setSortOrder(-1 as SortOrder);
  };

  // Modify the filter button click handler:
  const handleFilterClick = useCallback(() => {
    setShowFilterDropdown(!showFilterDropdown);
    if (!filterContentLoaded) {
      setFilterContentLoaded(true);
    }
  }, [showFilterDropdown, filterContentLoaded]);

  // Optimize these handlers with useCallback
  const handleStoreToggle = (store: 'ec' | 'vt', checked: boolean) => {
    console.log(`Toggle: ${store} -> ${checked}`);
    
    // 1. Direct DOM manipulation for immediate visual feedback
    const checkbox = document.getElementById(store === 'ec' ? 'ec-amz-checkbox' : 'vintage-checkbox');
    if (checkbox) {
      // Force the checkbox to visually update immediately
      (checkbox as HTMLInputElement).checked = checked;
    }
    
    // 2. Then update React state in background
    setTimeout(() => {
      if (store === 'ec') {
        setTempEcAmzEnabled(checked);
      } else {
        setTempVintageEnabled(checked);
      }
    }, 0);
  };

  // 2. Optimize the filterDropdownContent with better memoization
  const filterDropdownContent = useMemo(
    () => {
      // Only render if the dropdown is shown or content has been loaded before
      if (!filterContentLoaded && !showFilterDropdown) {
        return null;
      }
      
      return (
        <div className='container-fluid'>
          <div className='row mb-3'>
            <div className='col-md-8'>
              <h6 className='m-5'>By Category</h6>
              <div className='form-check mb-2'>
                <input
                  type='checkbox'
                  className='form-check-input'
                  id='cat-all'
                  checked={allCategoriesSelected}
                  onChange={(e) => handleToggleAllCategories(e.target.checked)}
                />
                <label className='form-check-label' htmlFor='cat-all'>
                  All Categories
                </label>
              </div>
              <div
                style={{
                  columnWidth: '180px',
                  columnGap: '10px',
                  maxHeight: '500px',
                  overflowY: 'auto',
                }}
              >
                {Object.keys(filterOptions).slice(0, showAllCategories ? undefined : 10).map((cat) => (
                  <div
                    key={cat}
                    style={{
                      padding: '4px',
                      breakInside: 'avoid',
                      marginBottom: '10px',
                    }}
                  >
                    <div className='form-check mb-4'>
                      <input
                        type='checkbox'
                        className='form-check-input'
                        id={`cat-${cat}`}
                        checked={filterOptions[cat].selected}
                        onChange={(e) => handleCategoryToggle(cat, e.target.checked)}
                      />
                      <label
                        style={{fontWeight: '700'}}
                        className='form-check-label'
                        htmlFor={`cat-${cat}`}
                      >
                        {cat} ({filterOptions[cat].count})
                      </label>
                    </div>
                    <div style={{marginLeft: '1rem'}}>
                      {Object.keys(filterOptions[cat].productTypes).map((pt) => (
                        <div key={pt} className='form-check mb-3'>
                          <input
                            type='checkbox'
                            className='form-check-input'
                            id={`pt-${cat}-${pt}`}
                            checked={filterOptions[cat].productTypes[pt]}
                            onChange={(e) => handleProductTypeToggle(cat, pt, e.target.checked)}
                          />
                          <label className='form-check-label' htmlFor={`pt-${cat}-${pt}`}>
                            {pt} ({filterOptions[cat].productTypeCounts[pt]})
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {Object.keys(filterOptions).length > 10 && !showAllCategories && (
                  <button 
                    className="btn btn-link btn-sm" 
                    onClick={() => setShowAllCategories(true)}
                  >
                    Show all ({Object.keys(filterOptions).length}) categories
                  </button>
                )}
              </div>
            </div>
            <div className='col-md-4' style={{borderLeft: '1px solid #666'}}>
              <h6 className='m-5'>By Brand</h6>
              <div className='form-check mb-2'>
                <input
                  type='checkbox'
                  className='form-check-input'
                  id='brand-all'
                  checked={allBrandsSelected}
                  onChange={(e) => handleToggleAllBrands(e.target.checked)}
                />
                <label className='form-check-label' htmlFor='brand-all'>
                  All Brands
                </label>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '4px',
                  marginLeft: '20px',
                }}
              >
                {Object.keys(brandFilterOptions).map((brand) => (
                  <div key={brand} className='form-check mb-1 mx-2' style={{padding: '2px'}}>
                    <input
                      type='checkbox'
                      className='form-check-input'
                      id={`brand-${brand}`}
                      checked={brandFilterOptions[brand].selected}
                      onChange={(e) => handleBrandToggle(brand, e.target.checked)}
                    />
                    <label className='form-check-label' htmlFor={`brand-${brand}`}>
                      {brand} ({brandFilterOptions[brand].count})
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <button className='btn btn-primary btn-sm' onClick={applyFilter}>
            Apply Filters
          </button>
        </div>
      );
    },
    [
      filterOptions, 
      brandFilterOptions, 
      allCategoriesSelected, 
      allBrandsSelected, 
      filterContentLoaded, 
      showFilterDropdown, 
      showAllCategories,
      handleCategoryToggle,
      handleProductTypeToggle,
      applyFilter
    ]
  );

  const isDirty =
    tempFromDate !== fromDate ||
    tempToDate !== toDate ||
    tempEcAmzEnabled !== ecAmzEnabled ||
    tempVintageEnabled !== vintageEnabled ||
    JSON.stringify(filterOptions) !== JSON.stringify(appliedFilters) ||
    JSON.stringify(brandFilterOptions) !== JSON.stringify(appliedBrandFilters)

  // --- Render ---
  return (
    <div
      className='card-body card text-body amazon-sales-table-wrapper'
      style={{backgroundColor: bodyBg, color: bodyText, minHeight: '100vh', padding: '1rem'}}
    >
      <Toast ref={toast} />
      <div className='card-header pb-4 pt-2'>
        <div className='row mb-3 align-items-center'>
          <div className='col-auto'>
            <div className='input-group input-group-sm me-3'>
              <button
                className='btn  button btn-secondary dropdown-toggle border-hover'
                type='button'
                style={{minWidth: '80px'}}
                onClick={handleFilterClick}
              >
                Filter
              </button>
              <div
                className='dropdown-menu p-3'
                style={{
                  display: showFilterDropdown ? 'block' : 'none',
                  width: '70vw',
                  maxHeight: '80vh',
                  overflowY: 'auto',
                  position: 'absolute',
                  left: '0',
                  top: '40px',
                  zIndex: 1000
                }}
              >
                <div className="d-flex justify-content-between mb-2">
                  <h5 className="mb-0">Filters</h5>
                  <button type="button" className="btn-close" onClick={() => setShowFilterDropdown(false)}></button>
                </div>
                
                {showFilterDropdown && filterDropdownContent}
              </div>
            </div>
          </div>
          <div className='d-flex col-auto'>
            <div className='input-group input-group-sm me-3'>
              <span className='input-group-text'>From</span>
              <input
                type='date'
                value={tempFromDate}
                onChange={(e) => setTempFromDate(e.target.value)}
                className='form-control bg-secondary'
              />
            </div>

            <div className='input-group input-group-sm'>
              <span className='input-group-text'>To</span>
              <input
                type='date'
                value={tempToDate}
                onChange={(e) => setTempToDate(e.target.value)}
                className='form-control bg-secondary'
              />
            </div>
          </div>

          <div className='col-auto d-flex flex-wrap align-items-center h-100 flex-grow-1 pl-4'>
            <div className='form-check form-check-inline me-4'>
              <input
                type='checkbox'
                checked={tempEcAmzEnabled}
                onChange={(e) => handleStoreToggle('ec', e.target.checked)}
                className='form-check-input'
                id='ec-amz-checkbox'
              />
              <label
                className='form-check-label cursor-pointer'
                htmlFor='ec-amz-checkbox'
                style={{color: 'skyblue', fontWeight: 'bold'}}
              >
                EC AMZ
              </label>
            </div>

            <div className='form-check form-check-inline'>
              <input
                type='checkbox'
                checked={tempVintageEnabled}
                onChange={(e) => handleStoreToggle('vt', e.target.checked)}
                className='form-check-input'
                id='vintage-checkbox'
              />
              <label
                className='form-check-label cursor-pointer'
                htmlFor='vintage-checkbox'
                style={{color: 'pink', fontWeight: 'bold'}}
              >
                VINTAGE
              </label>
            </div>
          </div>
          <div className='col-auto'>
            <button
              className={`btn btn-${isDirty ? 'warning' : 'primary'} btn-sm`}
              onClick={handleSubmit}
            >
              Submit
            </button>
          </div>
        </div>
      </div>
      <div

        style={{
          position: 'relative',
          backgroundColor: bodyBg,
          borderRadius: '8px',
          padding: '0.5rem',
        }}
      >
        <DataTable
          value={finalData}
          scrollable
          scrollHeight='700px'
          paginator
          rows={rows}
          first={first}
          onPage={(e) => setFirst(e.first)}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          rowsPerPageOptions={[10, 25, 50, 100]}
          expandedRows={expandedRows}
          onRowExpand={onRowExpand}
          onRowCollapse={onRowCollapse}
          rowExpansionTemplate={rowExpansionTemplate}
          sortField={sortField}
          sortOrder={sortOrder}
          onSort={onSort}
          style={{backgroundColor: bodyBg, color: bodyText}}
        >
          <Column expander style={{width: '2em', backgroundColor: bodyBg, color: (mode === 'light' ? 'var(--bs-gray-900)' : bodyText)}} />
          <Column
            header=''
            body={imageBodyTemplate}
            style={{width: '60px', backgroundColor: bodyBg, color: bodyText}}
          />
          <Column
            field='asin'
            header='ASIN'
            sortable={false}
            body={(rowData) => (rowData.asin === '__total__' ? 'TOTAL' : rowData.asin)}
            style={{
              width: '120px',
              backgroundColor: bodyBg,
              color: bodyText,
              fontSize: '14px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
            }}
          />
          <Column
            header='Brand'
            body={brandBodyTemplate}
            style={{
              width: '140px',
              backgroundColor: bodyBg,
              color: bodyText,
              fontSize: '14px',
              fontWeight: 'bold',
            }}
          />
          <Column
            header='Store'
            body={storeBodyTemplate}
            sortable={false}
            style={{
              width: '100px',
              backgroundColor: bodyBg,
              color: bodyText,
              fontSize: '12px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
            }}
          />
          <Column
            header='Units Sold'
            body={unitsAndStatsBodyTemplate}
            sortable
            sortField="quantity_sold"
            sortFunction={(event) => customSort(event)}
            style={{
              width: '210px',
              backgroundColor: bodyBg,
              color: bodyText,
              fontSize: '16px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              textAlign: 'center',
            }}
          />
          <Column
            header='Revenue'
            body={revenueBodyTemplate}
            sortable
            sortField="revenue"
            sortFunction={(event) => customSort(event)}
            style={{
              width: '150px',
              backgroundColor: bodyBg,
              color: bodyText,
              fontSize: '16px',
              fontWeight: 'bold',
            }}
          />
          <Column
            header='Profit'
            body={profitBodyTemplate}
            sortable
            sortField="profit"
            sortFunction={(event) => customSort(event)}
            style={{
              width: '150px',
              backgroundColor: bodyBg,
              color: bodyText,
              fontSize: '16px',
              fontWeight: 'bold',
            }}
          />
          <Column
            header='Margin'
            body={marginCombinedBodyTemplate}
            sortable
            sortField="margin"
            sortFunction={(event) => customSort(event)}
            style={{
              width: '120px',
              backgroundColor: bodyBg,
              color: bodyText,
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          />
          <Column
            header='ROI'
            body={roiBodyTemplate}
            sortable
            sortField="roi"
            sortFunction={(event) => customSort(event)}
            style={{
              width: '100px',
              backgroundColor: bodyBg,
              color: bodyText,
              fontSize: '16px',
              fontWeight: 'bold',
            }}
          />
          <Column
            header='ACOS (%)'
            body={acosBodyTemplate}
            sortable
            sortField="acos"
            sortFunction={(event) => customSort(event)}
            style={{
              width: '100px',
              backgroundColor: bodyBg,
              color: bodyText,
              fontSize: '16px',
              fontWeight: 'bold',
            }}
          />
          <Column
            header='TACOS'
            body={tacosBodyTemplate}
            sortable
            sortField="tacos"
            sortFunction={(event) => customSort(event)}
            style={{
              width: '100px',
              backgroundColor: bodyBg,
              color: bodyText,
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          />
          <Column
            header='ROAs'
            body={roasBodyTemplate}
            sortable
            sortField="roas"
            sortFunction={(event) => customSort(event)}
            style={{
              width: '100px',
              backgroundColor: bodyBg,
              color: bodyText,
              fontSize: '16px',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          />
        </DataTable>
        {loading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.1)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10,
            }}
          >
            <ProgressSpinner
              style={{width: '40px', height: '40px'}}
              strokeWidth='6'
              fill='var(--surface-ground)'
              animationDuration='.75s'
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default React.memo(AmazonSalesTable);
